import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ledger } from '../platform/browser.js';

/**
 * THE THING THAT STOPS ONE BROKEN FIELD TAKING THE WHOLE SCREEN.
 *
 * ⚠️ WRITTEN AFTER IT COST US, 31 Jul 2026. A flag came back from an API one release behind
 * and without `photoKeys`; `f.photoKeys.map(...)` threw during render, React unmounted the
 * tree, and **the item became permanently unopenable** — not a missing thumbnail, the entire
 * screen, every time, for as long as the flag existed. 0.1.88 fixed that one field. This is
 * the half that stops the NEXT one costing the same, and the next one is guaranteed: the web
 * bundle reaches Cloudflare in seconds while the API reaches Fly minutes later, so every
 * release has a window in which a new client asks an old server for a field it does not send.
 *
 * A CLASS COMPONENT, WHICH THIS CODEBASE OTHERWISE HAS NONE OF. `componentDidCatch` and
 * `getDerivedStateFromError` have no hook equivalent — there is no `useErrorBoundary`. This
 * is not a style lapse; it is the only way React offers.
 *
 * WHAT IT DOES NOT DO, stated because a boundary invites the opposite belief:
 *  - it does not catch errors in event handlers, promises or `setTimeout` — only rendering,
 *    lifecycle and constructors. A failed fetch still goes through `call()` and the ledger.
 *  - it does not RETRY anything by itself. Retrying an error you have not diagnosed is how a
 *    render loop is born, so the reset is a deliberate press.
 *  - it does not hide the failure. Every catch writes the ledger, which is Guardrail 10:
 *    nothing fails silently, and a boundary that swallowed its cause would be the most
 *    expensive silence in the app.
 */
interface Props {
  children: ReactNode;
  /**
   * Where this boundary sits, for the ledger entry: `screen:item-detail`, `panel:flags`.
   * The point of several boundaries is knowing WHICH part died, so this is required.
   */
  where: string;
  /** What the reader loses. "the condition panel", "this screen". */
  what: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /**
     * `notified: true` because the fallback below IS the notification — the user is looking
     * at it. Claiming otherwise would make the ledger lie about whether anyone was told.
     *
     * The component stack is carried as context: "which part of the screen" is the first
     * question asked of a render crash, and without it the entry is a heap of minified
     * frames from a production bundle.
     */
    ledger.record(error, `render:${this.props.where}`, {
      notified: true,
      // `context`, not a free-text detail: the ledger takes scalars, and the top few
      // frames answer "which part of the screen" without shipping a minified stack.
      context: {
        componentStack: (info.componentStack ?? '').trim().split('\n').slice(0, 4).join(' | '),
      },
    });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="error-boundary" role="alert">
        <p>
          <strong>{this.props.what} could not be shown.</strong>
        </p>
        {/* The message, not a swallowed apology: the reader is often the maintainer, and
            "something went wrong" has never once helped anyone. */}
        <p className="error-boundary-detail">{error.message}</p>
        <p>
          <button
            type="button"
            onClick={() => {
              this.setState({ error: null });
            }}
          >
            Try again
          </button>{' '}
          <small>This has been recorded. The rest of the app still works.</small>
        </p>
      </div>
    );
  }
}
